/**
 * FunctionNode - portable expression that maps values.
 *
 * `expression` is a standard number param with useExp. When useExp is on,
 * the expression is the getter and the slider provides `value` for local testing.
 * When adopted by a target, `value`/`min`/`max`/`host` re-bind to the target's context.
 *
 * compile() wraps the expression in a GetterFactory with an outer closure that
 * captures FunctionNode's custom params and event input live. The inner factory
 * follows the standard format so targets can re-bind with their own context.
 *
 * ## Context variables
 *
 * Target-local (re-bound when factory lands on a target):
 *   value  - the endpoint's stored value (slider on FunctionNode, target's value elsewhere)
 *   min, max, step - endpoint metadata
 *   host   - sibling endpoints on the host node
 *   t, bar, beat - transport time
 *
 * FunctionNode-specific (travel via outer closure, always read from FunctionNode):
 *   input  - last event payload value from eventIn (0 if no event received)
 *   <name> - custom param shorthand (e.g. `speed`), reads live from FunctionNode's store
 *
 * ## Endpoints
 * - expression (number, output, primary): slider value + compiled getter via useExp
 * - eventIn (event, input): receives events, evaluates expression, pushes to eventOut
 * - eventOut (event, output): emits evaluated result
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import type { Dependencies } from '@nodes/Dependencies';
import type { IEndpoint } from '@runtime/IEndpoint';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import { params, events } from '@/nodes/TypedEndpoints';
import { resolveConfigValue } from '@runtime/util';
import { logger } from '@/logging';

const endpoints = {
  ...params({
    expression: { min: 0, max: 1, step: 0.01, useExp: true, exp: 'value', isOutput: true, primary: true },
  }),
  ...events({
    eventIn: { isInput: true },
    eventOut: { isOutput: true },
  }),
};

export class FunctionNode extends BaseNode<typeof endpoints> {
  private expression = '';
  /** Mutable context — outer closure reads from this live each getter call */
  private ctx = { input: 0 as any, index: 0, iteration: 0 };

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();
    // Read expression from config metadata (useExp stores source as `exp`)
    const meta = this.deps.endpointApi.getMetadata('expression');
    this.expression = (meta as any)?.exp ?? '';
    if (this.expression) this.compile();
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    if (endpointId === 'expression') {
      // Check if expression changed (triggered by useExp toggle or exp edit)
      const meta = this.deps.endpointApi.getMetadata('expression');
      const expr = ((meta as any)?.exp as string) ?? '';
      if (expr !== this.expression) {
        this.expression = expr;
        this.compile();
      }
      // Slider value changes don't need recompilation
    } else {
      // Custom param added/removed/changed — recompile
      this.compile();
    }
  }

  handleEvent(payload: Payload, endpointId: string): void {
    if (endpointId !== 'eventIn') return;

    // Store event payload for expression access via `input` and `index`
    this.ctx.input = resolveConfigValue(payload);
    this.ctx.iteration = payload.iteration ?? 0;
    this.ctx.index = payload.index ?? 0;

    // Evaluate getter if active, otherwise pass through
    const result = this.deps.endpointApi.hasGetter('expression')
      ? this.deps.endpointApi.getValue('expression')
      : this.ctx.input;

    // If expression returns an object with `value`, spread metadata into payload
    // e.g. return { value: 60, velocity: 0.8, duration: 0.5 }
    if (result !== null && typeof result === 'object' && 'value' in result) {
      this.events.eventOut.emit({ ...payload, ...result });
    } else {
      this.events.eventOut.emit({ ...payload, value: result });
    }
  }

  onConnectedTo(endpointId: string, _targetNode: any, targetEndpoint: IEndpoint): void {
    if (endpointId !== 'expression') return;
    if (this.expression.trim()) return; // Don't override existing expression
    const get = (targetEndpoint as any).get;
    if (typeof get !== 'function') return;
    const expr = this.extractExpression(get);
    if (expr) {
      this.expression = expr;
      // Store expression back to config so it persists
      this.params.expression.set(this.params.expression.value);
    }
  }

  private extractExpression(getterFn: Function): string | null {
    const src = getterFn.toString();
    const match = src.match(/return\s+([\s\S]+?)\s*;?\s*\}\s*$/);
    return match ? match[1] : null;
  }

  private compile(): void {
    const source = this.expression;

    if (!source?.trim()) {
      this.deps.endpointApi.setGetFactory('expression', null);
      return;
    }

    const customIds = Object.keys(this.deps.endpointApi.getCustomParams());
    const paramDecls = customIds.map(id => `var ${id} = _p.${id}.value;`).join(' ');

    // Multi-line: user provides own `return`. Single-line: auto-wrapped.
    const isArrowFn = /^\s*(\([^)]*\)|[a-zA-Z_$]\w*)\s*=>/.test(source);
    const body = source.includes('\n') ? source
      : isArrowFn ? `return (${source})(dt);`
      : `return ${source};`;

    try {
      // Outer closure: captures FunctionNode's custom params + input context (live reads).
      // Inner: standard GetterFactory — target re-binds with its own local context.
      const factory = eval(
        `(function(_p, _ctx) {` +
        ` return function(_c) {` +
        `  return function(dt) {` +
        `   dt = dt ?? 0; var t = dt;` +
        `   var value = _c.value; var min = _c.min; var max = _c.max; var step = _c.step;` +
        `   var host = _c.host; var rnd = _c.rnd;` +
        `   var bar = Math.floor(t / (_c._ppq * _c._bpb)) + 1;` +
        `   var beat = Math.floor((t % (_c._ppq * _c._bpb)) / _c._ppq) + 1;` +
        `   var input = _ctx.input;` +
        `   var index = _ctx.index;` +
        `   var iteration = _ctx.iteration;` +
        `   ${paramDecls}` +
        `   ${body}` +
        `  };` +
        ` };` +
        `})`
      )(this.params, this.ctx);

      factory._source = source;
      // Set factory on fn endpoint — propagates to targets for re-binding
      this.deps.endpointApi.setGetFactory('expression', factory);
    } catch (e) {
      logger.error('FunctionNode', `compile failed: ${e}`);
      this.deps.endpointApi.setGetFactory('expression', null);
    }
  }
}

export const functionNodeDef: NodeDef<FunctionNode> = {
  type: 'Function',
  shortName: 'exp',
  info: 'Portable expression that maps values. Use x as input variable (e.g. x*2, Math.sin(x)).',
  domain: Domain.Data,
  endpoints,
  allowAddParam: true,
  ctor: FunctionNode,
};
